import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import {
	applySchoolsRspoDecisions,
	getSchoolsRspoDiff,
	SchoolsRspoDiffReport,
	SchoolsRspoDiffRow,
	SchoolsRspoScope,
} from '../../api/content-api';
import { RSPO_VOIVODESHIPS } from '../../constants/rspo-voivodeships';

type RowAction = 'add' | 'apply' | 'skip' | 'remove';

const ACTION_LABELS: Record<RowAction, string> = {
	add: 'Dodaj',
	apply: 'Zastosuj z RSPO',
	skip: 'Pomiń',
	remove: 'Usuń z rejestru edycji',
};

const ACTION_FILTER_OPTIONS: { value: '' | RowAction; label: string }[] = [
	{ value: '', label: 'Wszystkie akcje' },
	{ value: 'add', label: ACTION_LABELS.add },
	{ value: 'apply', label: ACTION_LABELS.apply },
	{ value: 'skip', label: ACTION_LABELS.skip },
	{ value: 'remove', label: ACTION_LABELS.remove },
];

const defaultActionForRow = (row: SchoolsRspoDiffRow): RowAction => {
	if (
		row.default_action === 'add' ||
		row.default_action === 'apply' ||
		row.default_action === 'skip' ||
		row.default_action === 'remove'
	) {
		return row.default_action;
	}
	if (row.type === 'new') return 'add';
	if (row.type === 'missing_from_rspo') return 'remove';
	if (row.type === 'changed') return 'apply';
	return 'skip';
};

type AddressParts = {
	name?: string;
	street?: string;
	building?: string;
	apartment?: string;
	zip?: string;
	city?: string;
};

const formatSchoolBlock = (parts: AddressParts, emptyLabel = '—'): React.ReactNode => {
	const name = (parts.name || '').trim();
	const street = (parts.street || '').trim();
	const building = (parts.building || '').trim();
	const apartment = (parts.apartment || '').trim();
	const zip = (parts.zip || '').trim();
	const city = (parts.city || '').trim();

	if (!name && !street && !building && !zip && !city) {
		return emptyLabel;
	}

	const streetLine = [street, building].filter(Boolean).join(' ');
	const streetFull = streetLine ? (apartment ? `${streetLine}/${apartment}` : streetLine) : apartment ? apartment : '';
	const cityLine = [zip, city].filter(Boolean).join(' ');

	return (
		<Box
			component='span'
			sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.45 }}
		>
			{name ? (
				<Box
					component='span'
					sx={{ display: 'block', fontWeight: 500 }}
				>
					{name}
				</Box>
			) : null}
			{streetFull ? (
				<Box
					component='span'
					sx={{ display: 'block' }}
				>
					{streetFull}
				</Box>
			) : null}
			{cityLine ? (
				<Box
					component='span'
					sx={{ display: 'block' }}
				>
					{cityLine}
				</Box>
			) : null}
		</Box>
	);
};

const localAddressFromRow = (row: SchoolsRspoDiffRow): AddressParts => ({
	name: row.local_name,
	street: row.local_street,
	building: row.local_building,
	apartment: row.local_apartment,
	zip: row.local_zip,
	city: row.local_city,
});

const remoteAddressFromRow = (row: SchoolsRspoDiffRow): AddressParts => ({
	name: row.remote_name,
	street: row.remote_street,
	building: row.remote_building,
	apartment: row.remote_apartment,
	zip: row.remote_zip,
	city: row.remote_city,
});

type Props = {
	open: boolean;
	onClose: () => void;
};

const SchoolsRspoRefreshDialog = ({ open, onClose }: Props) => {
	const { token } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const [county, setCounty] = useState('');
	const [voivodeship, setVoivodeship] = useState('');
	const [loading, setLoading] = useState(false);
	const [applying, setApplying] = useState(false);
	const [report, setReport] = useState<SchoolsRspoDiffReport | null>(null);
	const [actions, setActions] = useState<Record<number, RowAction>>({});
	const [actionFilter, setActionFilter] = useState<'' | RowAction>('');

	const rowAction = useCallback(
		(row: SchoolsRspoDiffRow): RowAction => actions[row.rspo] || defaultActionForRow(row),
		[actions],
	);

	const scope = useCallback((): SchoolsRspoScope => {
		const next: SchoolsRspoScope = {};
		const countyTrimmed = county.trim();
		const voivTrimmed = voivodeship.trim();
		if (countyTrimmed) next.county = countyTrimmed;
		if (voivTrimmed) next.voivodeship = voivTrimmed;
		return next;
	}, [county, voivodeship]);

	const loadReport = useCallback(async () => {
		if (!token) return;
		setLoading(true);
		try {
			const data = await getSchoolsRspoDiff(token, scope());
			setReport(data);
			const next: Record<number, RowAction> = {};
			for (const row of data.rows) {
				next[row.rspo] = defaultActionForRow(row);
			}
			setActions(next);
			setActionFilter('');
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			const detail = ax?.response?.data?.message;
			setMsg({
				msg: detail
					? `Nie udało się wczytać różnic RSPO: ${detail}`
					: 'Nie udało się wczytać różnic RSPO (brak odpowiedzi API MEN lub błąd sieci). Sprawdź połączenie lub spróbuj z filtrem województwa / powiatu.',
				severity: 'error',
			});
			setReport(null);
		} finally {
			setLoading(false);
		}
	}, [token, scope, setMsg]);

	const handleApply = async () => {
		if (!token || !report) return;
		const decisions = report.rows
			.filter((row) => (actions[row.rspo] || 'skip') !== 'skip')
			.map((row) => ({ rspo: row.rspo, action: actions[row.rspo] || 'skip' }));
		if (decisions.length === 0) {
			setMsg({
				msg: 'Brak pozycji do zastosowania — wybierz co najmniej jedną akcję inną niż „Pomiń”.',
				severity: 'warning',
			});
			return;
		}
		setApplying(true);
		try {
			const result = await applySchoolsRspoDecisions(token, decisions, scope());
			setMsg({
				msg: `Zastosowano ${result.applied} zmian, pominięto ${result.skipped}.`,
				severity: 'success',
			});
			await loadReport();
		} catch {
			setMsg({ msg: 'Nie udało się zastosować zmian RSPO.', severity: 'error' });
		} finally {
			setApplying(false);
		}
	};

	const typeLabel = (type: SchoolsRspoDiffRow['type']) => {
		if (type === 'new') return 'Nowa';
		if (type === 'changed') return 'Zmiana';
		if (type === 'missing_from_rspo') return 'Brak w rejestrze';
		return 'Ręczna korekta';
	};

	const changedFieldsLabel = (row: SchoolsRspoDiffRow) => {
		if (row.fields_labels) return row.fields_labels;
		if (!row.fields) return null;
		const map: Record<string, string> = {
			name: 'nazwa',
			city: 'miasto',
			street: 'ulica',
			building: 'budynek',
			apartment: 'mieszkanie',
			zip_code: 'kod pocztowy',
			wojewodztwo: 'województwo',
			powiat: 'powiat',
		};
		return row.fields
			.split(',')
			.map((f) => map[f.trim()] || f.trim())
			.join(', ');
	};

	const visibleRows = report?.rows.filter((row) => actionFilter === '' || rowAction(row) === actionFilter) ?? [];

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='lg'
			fullWidth
		>
			<DialogTitle>Odświeżenie szkół z RSPO</DialogTitle>
			<DialogContent>
				<Typography
					variant='body2'
					sx={{ mb: 2 }}
				>
					Porównanie bieżącej edycji (skopiowanej z poprzedniej) ze świeżymi danymi RSPO — mapowanie po numerze RSPO.
					Szkoły oznaczone ręcznie nie są nadpisywane automatycznie. Pozycje „Brak w rejestrze” to placówki zlikwidowane
					lub niedostępne w API w wybranym zakresie (województwo/powiat z RSPO przy szkole, nie z kodu pocztowego; „0
					uczniów” nie oznacza usunięcia). Domyślnie można usunąć je z edycji.
				</Typography>
				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
					<FormControl
						size='small'
						sx={{ minWidth: 220 }}
					>
						<InputLabel id='rspo-voivodeship-filter'>Województwo (opcjonalnie)</InputLabel>
						<Select
							labelId='rspo-voivodeship-filter'
							label='Województwo (opcjonalnie)'
							value={voivodeship}
							onChange={(e) => setVoivodeship(e.target.value)}
						>
							<MenuItem value=''>Cała Polska</MenuItem>
							{RSPO_VOIVODESHIPS.map((item) => (
								<MenuItem
									key={item.code}
									value={item.code}
								>
									{item.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						size='small'
						label='Filtr powiatu (opcjonalnie)'
						placeholder='np. radzyński'
						value={county}
						onChange={(e) => setCounty(e.target.value)}
						sx={{ minWidth: 220 }}
					/>
					<Button
						variant='contained'
						onClick={() => loadReport()}
						disabled={loading}
					>
						{loading ? <CircularProgress size={22} /> : 'Wczytaj różnice'}
					</Button>
				</Box>
				{report?.summary && (
					<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
						{report.summary.previous_edition != null && (
							<Chip
								size='small'
								label={`Poprzednia edycja: ${report.summary.previous_edition}`}
							/>
						)}
						{report.summary.voivodeship_filter && (
							<Chip
								size='small'
								label={`Województwo: ${report.summary.voivodeship_filter}`}
							/>
						)}
						{report.summary.county_filter && (
							<Chip
								size='small'
								label={`Powiat: ${report.summary.county_filter}`}
							/>
						)}
						<Chip
							size='small'
							label={`Nowe: ${report.summary.new_from_rspo}`}
						/>
						<Chip
							size='small'
							label={`Zmiany: ${report.summary.changed}`}
						/>
						<Chip
							size='small'
							label={`Domyślnie pominięte: ${report.summary.default_skipped ?? report.summary.manual_skipped}`}
						/>
						<Chip
							size='small'
							label={`Brak w rejestrze: ${report.summary.missing_from_rspo ?? 0}`}
						/>
						<Chip
							size='small'
							label={`Do importu (uczniowie>0): ${report.summary.total_rspo}`}
						/>
						{typeof report.summary.total_rspo_present === 'number' && (
							<Chip
								size='small'
								label={`W rejestrze API: ${report.summary.total_rspo_present}`}
							/>
						)}
					</Box>
				)}
				{report && report.rows.length > 300 && (
					<Typography
						variant='body2'
						color='warning.main'
						sx={{ mb: 1 }}
					>
						Raport ma {report.rows.length} wierszy — przeglądarka może chwilę reagować wolniej. Przy filtrze województwa
						typ „Brak w rejestrze” powinien być niewielki; bardzo duża liczba to sygnał błędu zakresu.
					</Typography>
				)}
				{report && report.rows.length === 0 && !loading && (
					<Typography color='text.secondary'>Brak różnic w wybranym zakresie.</Typography>
				)}
				{report && report.rows.length > 0 && (
					<>
						<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
							<FormControl
								size='small'
								sx={{ minWidth: 220 }}
							>
								<InputLabel id='rspo-action-filter'>Filtr akcji</InputLabel>
								<Select
									labelId='rspo-action-filter'
									label='Filtr akcji'
									value={actionFilter}
									onChange={(e) => setActionFilter(e.target.value as '' | RowAction)}
								>
									{ACTION_FILTER_OPTIONS.map((opt) => (
										<MenuItem
											key={opt.value || 'all'}
											value={opt.value}
										>
											{opt.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							{actionFilter !== '' && (
								<Typography
									variant='body2'
									color='text.secondary'
								>
									Pokazano {visibleRows.length} z {report.rows.length} wierszy ({ACTION_LABELS[actionFilter]})
								</Typography>
							)}
						</Box>
						{visibleRows.length === 0 && (
							<Typography
								color='text.secondary'
								sx={{ mb: 1 }}
							>
								Brak wierszy z akcją „{ACTION_LABELS[actionFilter as RowAction]}” — zmień filtr akcji.
							</Typography>
						)}
						<Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
							<Table
								size='small'
								stickyHeader
							>
								<TableHead>
									<TableRow>
										<TableCell>Typ</TableCell>
										<TableCell>RSPO</TableCell>
										<TableCell sx={{ minWidth: 200 }}>Lokalnie (edycja)</TableCell>
										<TableCell sx={{ minWidth: 200 }}>RSPO (świeże)</TableCell>
										<TableCell sx={{ minWidth: 140 }}>Różnica w polach</TableCell>
										<TableCell sx={{ minWidth: 160 }}>Akcja</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{visibleRows.map((row) => {
										const diffLabel = changedFieldsLabel(row);
										return (
											<TableRow key={row.rspo}>
												<TableCell>{typeLabel(row.type)}</TableCell>
												<TableCell>{row.rspo}</TableCell>
												<TableCell>{formatSchoolBlock(localAddressFromRow(row))}</TableCell>
												<TableCell>{formatSchoolBlock(remoteAddressFromRow(row))}</TableCell>
												<TableCell>
													{diffLabel ? (
														<Typography
															variant='body2'
															component='span'
														>
															{diffLabel}
														</Typography>
													) : (
														'—'
													)}
												</TableCell>
												<TableCell>
													<FormControl
														size='small'
														fullWidth
													>
														<InputLabel id={`rspo-action-${row.rspo}`}>Akcja</InputLabel>
														<Select
															labelId={`rspo-action-${row.rspo}`}
															label='Akcja'
															value={actions[row.rspo] || 'skip'}
															disabled={row.type === 'manual_skip'}
															onChange={(e) =>
																setActions((prev) => ({
																	...prev,
																	[row.rspo]: e.target.value as RowAction,
																}))
															}
														>
															{row.type === 'new' && <MenuItem value='add'>Dodaj</MenuItem>}
															{row.type === 'changed' && <MenuItem value='apply'>Zastosuj z RSPO</MenuItem>}
															{row.type === 'missing_from_rspo' && (
																<MenuItem value='remove'>Usuń z rejestru edycji</MenuItem>
															)}
															<MenuItem value='skip'>Pomiń</MenuItem>
														</Select>
													</FormControl>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Zamknij</Button>
				<Button
					variant='contained'
					color='primary'
					disabled={!report || applying || loading}
					onClick={() => handleApply()}
				>
					{applying ? <CircularProgress size={22} /> : 'Zastosuj wybrane'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SchoolsRspoRefreshDialog;
